import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import StyledButton from '../buttons/StyledButton';
import CircleCheck from '../icons/CircleCheck';
import CircleInvalid from '../icons/CircleInvalid';

import styles from '../../../styles/animations/explanation-feedback.module.css';

interface ExplanationFeedbackProps {
    className?: string;
    valid?: boolean;
    value?: boolean;
    answerDuration?: number;
    answerPoints?: number;
    question?: {
        validAnswerLabel?: string;
        explanation?: string;
    };
    gameId?: string;
    questionsGroupId?: string;
    onAnimationComplete?: () => void;
}

function ExplanationFeedback({
    className = null,
    valid = null,
    value = null,
    answerDuration = null,
    answerPoints = null,
    question = null,
    onAnimationComplete = null,
}: ExplanationFeedbackProps) {
    const { validAnswerLabel = null, explanation = null } = question || {};

    const onNextQuestionClicked = useCallback(() => {
        if (onAnimationComplete !== null) {
            onAnimationComplete();
        }
    }, [onAnimationComplete]);

    const isCorrectAnswer = valid === true;
    const isIncorrectAnswer = valid === false;
    const hasTimedOut = valid === false && value === null;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.visible]: valid !== null,
                    [styles.valid]: isCorrectAnswer,
                    [styles.invalid]: isIncorrectAnswer,
                    [styles.timedOut]: hasTimedOut,
                },
                className,
            ])}
        >
            <div className={styles.content}>
                <div className={styles.result}>
                    <div className={styles.text}>
                        {isCorrectAnswer ? (
                            <div className={styles.validity}>
                                <CircleCheck className={styles.icon} />
                                <h3 className={styles.title}>
                                    <FormattedMessage
                                        defaultMessage="Good answer!"
                                        description="Explanation Feedback title"
                                    />
                                </h3>
                            </div>
                        ) : null}
                        {isIncorrectAnswer ? (
                            <div className={styles.validity}>
                                <CircleInvalid
                                    className={classNames([styles.icon, styles.wrong])}
                                />
                                <h3 className={styles.title}>
                                    <FormattedMessage
                                        defaultMessage="Wrong answer"
                                        description="Explanation Feedback title"
                                    />
                                </h3>
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.stats}>
                        {answerPoints !== null ? (
                            <div className={styles.points}>
                                {answerPoints} point{answerDuration > 1 ? 's' : null}
                            </div>
                        ) : null}
                    </div>

                    {validAnswerLabel !== null || explanation !== null ? (
                        <div className={styles.box}>
                            {validAnswerLabel !== null ? (
                                <h4 className={styles.validAnswerTitle}>
                                    <FormattedMessage
                                        defaultMessage="Correct answer :"
                                        description="Rhone Academy - Correct answer"
                                    />
                                </h4>
                            ) : null}
                            {validAnswerLabel !== null ? (
                                <p className={styles.validAnswerLabel}>{validAnswerLabel}</p>
                            ) : null}
                            {explanation !== null ? (
                                <p className={styles.explanation}>{explanation}</p>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className={styles.next}>
                    <StyledButton className={styles.button} onClick={onNextQuestionClicked}>
                        <FormattedMessage
                            defaultMessage="Next question"
                            description="Feedback Button"
                        />
                    </StyledButton>
                </div>
            </div>
        </div>
    );
}

// ExplanationFeedback.propTypes = propTypes;

export default ExplanationFeedback;
